import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NavbarMenu from '@/components/ui/navbar';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { ScheduleButton } from '@/components/ScheduleButton';
import { useLanguage } from '@/contexts/LanguageContext';
import { getLocalizedRoute } from '@/lib/routes';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();

  const isActive = (path: string) => location.pathname === path;

  const menuItems = [
    { 
      id: 1,
      title: t.home, 
      url: getLocalizedRoute('home', language),
      dropdown: false
    },
    { 
      id: 2,
      title: t.about, 
      url: getLocalizedRoute('about', language),
      dropdown: false
    },
    { 
      id: 3,
      title: t.services, 
      url: getLocalizedRoute('services', language),
      dropdown: true,
      items: [
        { id: 31, title: t.smartPricingTitle, url: getLocalizedRoute('smartPricing', language) },
        { id: 32, title: t.consultingTitle, url: getLocalizedRoute('consulting', language) },
        { id: 33, title: t.researchTitle, url: getLocalizedRoute('research', language) }
      ]
    },
    { 
      id: 4,
      title: t.blog, 
      url: getLocalizedRoute('blog', language),
      dropdown: false
    },
    { 
      id: 5,
      title: t.contact, 
      url: getLocalizedRoute('contact', language),
      dropdown: false
    },
  ];

  return (
    <header className="bg-background shadow-soft sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to={getLocalizedRoute('home', language)} className="flex items-center">
            <img 
              src="/lovable-uploads/0b1132d5-c978-428c-b4a4-cb6560dab03b.png" 
              alt="PriceZing" 
              className="h-8 w-auto"
              width="141"
              height="32"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <NavbarMenu list={menuItems} />
          </div>

          <div className="hidden md:flex items-center space-x-4">
            <LanguageSwitcher />
            <ScheduleButton
              locale={language}
              variant="default"
              size="sm"
              source="header"
              intent="demo"
              className="header-cta"
            />
          </div>

            <div className="md:hidden flex items-center space-x-2">
              <LanguageSwitcher />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <MenuIcon className="h-5 w-5" />}
              </Button>
            </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-border">
            <nav className="flex flex-col space-y-4">
              {menuItems.map((item) => (
                <div key={item.title}>
                  {item.dropdown ? (
                    <>
                      <Link
                        to={item.url}
                        className={`text-sm font-medium transition-colors hover:text-primary ${
                          isActive(item.url) ? 'text-primary' : 'text-muted-foreground'
                        }`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.title}
                      </Link>
                      <div className="ml-4 mt-2 space-y-2">
                        {item.items?.map((subItem) => (
                          <Link
                            key={subItem.title}
                            to={subItem.url}
                            className={`block text-sm transition-colors hover:text-primary ${
                              isActive(subItem.url) ? 'text-primary font-medium' : 'text-muted-foreground'
                            }`}
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    </>
                  ) : (
                    <Link
                      to={item.url}
                      className={`text-sm font-medium transition-colors hover:text-primary ${
                        isActive(item.url) ? 'text-primary' : 'text-muted-foreground'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.title}
                    </Link>
                  )}
                </div>
              ))}
              <ScheduleButton
                locale={language}
                variant="default"
                size="sm"
                source="header-mobile"
                intent="demo"
                className="header-mobile-cta w-fit"
              />
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};